import { useAppStore } from "@/stores";
import useSWR from "swr";
import { getAll, getDeals, getDealstageAvgTimesByCompanyId } from "@/lib/supabase";
import { Loader } from "@/components/Loader";
// import { LoadingError } from "@/components/LoadingError";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
} from "lucide-react";
import { getDealOwner } from "@revelate/utils";
import { getAPI } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectValue, SelectItem } from "@/components/ui/select";
import { getDealEmails } from "@/lib/supabase";
import {
  evaluateRiskPipelineRisk,
  formatCurrencyValue,
  prepareDealsForRiskAssessment,
} from "../lib/risk";
import { AtRiskDealsDataTable } from "../components/AtRiskDealsDataTable";
import { AtRiskDealDetails } from "../components/AtRiskDealDetails";
import { RiskHighlightCard } from "../components/RiskHighlightCard";
import { RiskBreakdownChart } from "../components/charts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SectionCard } from "../components/SectionCard";
import { usePostHog } from "@posthog/react";
import { usePageTimeTracking } from "@/lib/posthog/usePageTimeTracking";

const DEALS_TABLE_COUNT = 5;

type DataBreakdown =
  | "by_pipeline"
  // | "by_product"
  | "by_month"
  | "by_sales_rep"
  // | "by_segment"
  ;

export function RevenueIntelligencePipelineRisk() {
  const [view, setView] = useState<"dashboard" | "deals" | "deal">("deals");
  const [viewingDealId, setViewingDealId] = useState<number | null>(null);
  const [pipelineRiskBreakdown, setPipelineRiskBreakdown] = useState<DataBreakdown>("by_pipeline");
  const [globalSelectedPipeline, setGlobalSelectedPipeline] = useState<string>("all");
  const [globalSelectedOwner, setGlobalSelectedOwner] = useState<string>("all");
  const [isAtRiskDealsModalOpen, setIsAtRiskDealsModalOpen] = useState<boolean>(false);
  const [atRiskDealsModalChoice, setAtRiskDealsModalChoice] = useState<null | "low" | "medium" | "high">(null);

  const { currentUser, companyCurrency } = useAppStore((state) => state);

  const posthog = usePostHog();
  usePageTimeTracking("l2_pipeline_risk", posthog);

  const {
    data: allOpenDeals,
    // error: allOpenDealsError,
    // isLoading: isLoadingAllOpenDeals,
  } = useSWR(
    [
      'query:allOpenDeals',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getDeals(companyId, {
      onlyOpen: true,
      nonZeroValue: true,
      includeEmails: true,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: currencies,
    // error: currenciesError,
    isLoading: isLoadingCurrencies,
  } = useSWR(
    [
      'query:currencies',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getAll("currencies", companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const {
    data: pipelines,
    // error: pipelinesError,
    // isLoading: isLoadingPipelines,
  } = useSWR(
    [
      'query:pipelines',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getAPI(`providers/hubspot/pipelines/${companyId}`),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const pipelinesForSelect = pipelines && pipelines.results
    ? [
      {
        value: "all",
        label: "All Pipelines",
      },
      ...pipelines.results
        .map((p: any) => ({
          value: p.id,
          label: p.label,
        })),
    ]
    : []

  const {
    data: dealstageAvgTimes,
    // error: dealstageAvgTimesError,
    // isLoading: isLoadingdealstageAvgTimes,
  } = useSWR(
    [
      'query:dealstageAvgTimes',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => companyId
      ? getDealstageAvgTimesByCompanyId(parseInt(companyId, 10))
      : [],
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  let ownersForSelect = [{
    value: "all",
    label: "All Sales Reps",
  }];

  if (allOpenDeals) {
    const openDealOwners = allOpenDeals.map(d => getDealOwner(d));

    const uniqueOwnersMap = new Map();
    openDealOwners.forEach(owner => {
      if (owner?.id && !uniqueOwnersMap.has(owner.id)) {
        uniqueOwnersMap.set(owner.id, owner);
      }
    });
    const uniqueOwnersArray = Array.from(uniqueOwnersMap.values());

    ownersForSelect = [
      ...ownersForSelect,
      ...uniqueOwnersArray.map(owner => ({
        value: owner.id,
        label: `${owner.first_name} ${owner.last_name}`,
      }))
    ];
  }

  const {
    data: dealEmails,
    // error: dealEmailsError,
    // isLoading: isLoadingDealEmails,
  } = useSWR(
    [
      'query:dealEmails',
      viewingDealId,
    ],
    ([_, dealId]) => dealId === null ? null : getDealEmails(dealId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  if (
    // isLoadingAllOpenDeals ||
    // !allOpenDeals
    isLoadingCurrencies
  ) return <Loader />;

  // if (
  //   allOpenDealsError
  // ) return <LoadingError />;

  const hubspotDealsForPipelineRiskAndCoverage = prepareDealsForRiskAssessment(
    allOpenDeals,
    pipelines,
    // @ts-ignore
    currencies,
    currentUser?.company,
    dealstageAvgTimes,
  );

  const {
    deals: dealsWithPipelineRisk,
    riskBreakdownChartData: chartDataPipelineRiskBreakdown,
    lowRiskDeals,
    lowRiskDealsTotalValue,
    lowRiskDealsTotalValuePctOfAll,
    mediumRiskDeals,
    mediumRiskDealsTotalValue,
    mediumRiskDealsTotalValuePctOfAll,
    highRiskDeals,
    highRiskDealsTotalValue,
    highRiskDealsTotalValuePctOfAll,
  } = evaluateRiskPipelineRisk(
    hubspotDealsForPipelineRiskAndCoverage,
    currentUser?.company_id,
    pipelines?.results ?? [],
    pipelineRiskBreakdown,
    globalSelectedPipeline,
    globalSelectedOwner,
    "largest",
    dealstageAvgTimes,
    companyCurrency,
  );

  return (
    <>
      <div>
        {view === "deals" && (
          <div className="space-y-4">
            <Card className="border-none bg-transparent">
              <CardContent className="px-0">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-row justify-between items-center">
                    <h1>Revenue At Risk</h1>
                    {/* Global filters */}
                    <div className="flex flex-row justify-end items-center gap-x-3">
                      <p className="text-sm text-gray-500">Filters</p>
                      <div className="flex flex-row gap-2">
                        <div className="flex flex-row gap-2 items-center">
                          <Select
                            value={globalSelectedPipeline}
                            onValueChange={v => setGlobalSelectedPipeline(v)}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {pipelinesForSelect.map((p: any) => (
                                <SelectItem key={p.value} value={p.value}>
                                  {p.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex flex-row gap-2 items-center">
                          <Select
                            value={globalSelectedOwner}
                            onValueChange={v => setGlobalSelectedOwner(v)}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ownersForSelect.map((owner: any) => (
                                <SelectItem key={owner.value} value={owner.value}>
                                  {owner.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col lg:grid lg:grid-cols-3 gap-2 w-full pb-4">
                    <RiskHighlightCard
                      title="High risk"
                      headline={`${formatCurrencyValue(highRiskDealsTotalValue, companyCurrency, 1)}`}
                      text={`${highRiskDealsTotalValuePctOfAll.toFixed(1)}% of total exposure`}
                      //change={0}
                      //isChangeGood={true}
                      //changeDescription="vs 30d ago"
                      onClick={() => {
                        if (!highRiskDeals.length) return;
                        setAtRiskDealsModalChoice("high");
                        setIsAtRiskDealsModalOpen(true);
                      }}
                    />
                    <RiskHighlightCard
                      title="Medium risk"
                      headline={`${formatCurrencyValue(mediumRiskDealsTotalValue, companyCurrency, 1)}`}
                      text={`${mediumRiskDealsTotalValuePctOfAll.toFixed(1)}% of total exposure`}
                      //change={0}
                      //isChangeGood={true}
                      //changeDescription="vs 30d ago"
                      onClick={() => {
                        if (!mediumRiskDeals.length) return;
                        setAtRiskDealsModalChoice("medium");
                        setIsAtRiskDealsModalOpen(true);
                      }}
                    />
                    <RiskHighlightCard
                      title="Low risk"
                      headline={`${formatCurrencyValue(lowRiskDealsTotalValue, companyCurrency, 1)}`}
                      text={`${lowRiskDealsTotalValuePctOfAll.toFixed(1)}% of total exposure`}
                      //change={0}
                      //isChangeGood={true}
                      //changeDescription="vs 30d ago"
                      onClick={() => {
                        if (!lowRiskDeals.length) return;
                        setAtRiskDealsModalChoice("low");
                        setIsAtRiskDealsModalOpen(true);
                      }}
                    />
                  </div>

                  <SectionCard className="col-span-3">
                    <div className="flex flex-col gap-y-4">
                      <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                        <div className="w-full flex flex-row justify-between mb-10">
                          {/* Header */}
                          <div>
                            <p className="font-medium text-sm">
                              Risk {pipelineRiskBreakdown === "by_pipeline" ? "Per Pipeline" : pipelineRiskBreakdown === "by_month" ? "Per Month" : pipelineRiskBreakdown === "by_sales_rep" ? "Per Sales Rep" : ""}
                            </p>
                          </div>
                          {/* Breakdown tabs */}
                          <div className="flex flex-row gap-x-2 items-center">
                            <Tabs
                              value={pipelineRiskBreakdown}
                              onValueChange={v => setPipelineRiskBreakdown(v as DataBreakdown)}
                            >
                              <TabsList >
                                <TabsTrigger
                                  value="by_pipeline"
                                  className="text-xs"
                                >
                                  By pipeline
                                </TabsTrigger>
                                <TabsTrigger
                                  value="by_sales_rep"
                                  className="text-xs"
                                >
                                  By sales rep
                                </TabsTrigger>
                                <TabsTrigger
                                  value="by_month"
                                  className="text-xs"
                                >
                                  By month
                                </TabsTrigger>
                              </TabsList>
                            </Tabs>
                          </div>
                        </div>

                        <div className="pb-4">
                          <RiskBreakdownChart
                            data={chartDataPipelineRiskBreakdown}
                            breakdown={pipelineRiskBreakdown}
                            selectedPipeline={globalSelectedPipeline}
                            setSelectedPipeline={setGlobalSelectedPipeline}
                            selectedOwner={globalSelectedOwner}
                            setSelectedOwner={setGlobalSelectedOwner}
                            companyCurrency={companyCurrency}
                          />
                        </div>
                      </div>
                    </div>
                  </SectionCard>

                  <AtRiskDealsDataTable
                    deals={dealsWithPipelineRisk.slice(0, DEALS_TABLE_COUNT)}
                    onClickDeal={(dealId) => {
                      setViewingDealId(dealId)
                      setView("deal")
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {view === "deal" && viewingDealId !== null && (
          <div className="space-y-1">
            <Button
              onClick={() => {
                setView("deals");
                setViewingDealId(null);
              }}
              variant="ghost"
              className="bg-gray-100"
            >
              <div className="flex flex-row gap-2 items-center">
                <ArrowLeft size="14" />
                <span>
                  Back
                </span>
              </div>
            </Button>
            <AtRiskDealDetails
              deal={dealsWithPipelineRisk.find(d => d.id === viewingDealId)}
              dealEmails={dealEmails}
              riskCategory="pipelineRisk"
            />
          </div>
        )}
      </div>

      {/* Modal: At-risk deals */}
      <Dialog
        open={isAtRiskDealsModalOpen}
        onOpenChange={v => setIsAtRiskDealsModalOpen(v)}
      >
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>
              {atRiskDealsModalChoice === "high"
                ? "High risk deals"
                : atRiskDealsModalChoice === "medium"
                  ? "Medium risk deals"
                  : atRiskDealsModalChoice === "low"
                    ? "Low risk deals"
                    : "Deals"
              }
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="max-h-[480px] overflow-y-scroll">
            {atRiskDealsModalChoice === "high" ? (
              <AtRiskDealsDataTable
                deals={highRiskDeals.slice(0, 5)}
                showTitle={false}
              />
            ) : atRiskDealsModalChoice === "medium" ? (
              <AtRiskDealsDataTable
                deals={mediumRiskDeals.slice(0, 5)}
                showTitle={false}
              />
            ) : atRiskDealsModalChoice === "low" ? (
              <AtRiskDealsDataTable
                deals={lowRiskDeals.slice(0, 5)}
                showTitle={false}
              />
            ) : null}
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  );
}
